import type { ApiError } from "@/types/api";

const TECHNICAL_ERROR_MARKERS = [
  "Validation failed for argument",
  "org.springframework",
  "Field error in object",
  "java.lang.",
  "MethodArgumentNotValidException",
];

const GENERIC_HTTP_ERRORS = new Set([
  "internal server error",
  "bad request",
  "not found",
  "conflict",
  "unauthorized",
  "forbidden",
]);

const GATEWAY_ERROR_PREFIX =
  /^[\w-]+\s+(\d{3})\s+[\w_]+:\s*/i;

/** Pull the last human-readable Spring validation message from a raw error string. */
function extractSpringValidationMessage(raw: string): string | null {
  const matches = [...raw.matchAll(/default message \[([^\]]+)\]/g)];
  for (let i = matches.length - 1; i >= 0; i--) {
    const msg = matches[i][1]?.trim();
    if (!msg) continue;
    if (msg.includes(" ") || msg.length > 12) return msg;
  }
  return null;
}

function isTechnicalErrorMessage(message: string): boolean {
  return TECHNICAL_ERROR_MARKERS.some((marker) => message.includes(marker));
}

function tryParseJsonObject(raw: string): Record<string, unknown> | null {
  const start = raw.indexOf("{");
  if (start === -1) return null;
  try {
    const parsed: unknown = JSON.parse(raw.slice(start));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function readStringField(body: Record<string, unknown>, key: string): string | null {
  const value = body[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readStatus(body: Record<string, unknown>, fallback?: number): number | undefined {
  const value = body.status;
  if (typeof value === "number" && value > 0) return value;
  return fallback;
}

function friendlyPathMessage(path: string): string | null {
  if (path.includes("/api/ops/branches")) {
    return "WhatsApp branch registration failed. Please verify the phone number and credentials, then try again.";
  }
  if (path.includes("/api/ops/routing-rules")) {
    return "WhatsApp message routing could not be configured. Please try again.";
  }
  if (path.includes("/chatgateway/whatsapp")) {
    return "WhatsApp setup failed. Please check your configuration and try again.";
  }
  if (path.includes("/chatgateway/communication-channel")) {
    return "The communication channel could not be synced. Please try again.";
  }
  if (path.includes("/chatgateway/legal-entity")) {
    return "The legal entity could not be synced. Please try again.";
  }
  if (path.includes("/chatgateway/branch")) {
    return "The branch could not be synced. Please try again.";
  }
  if (path.includes("/chatgateway/users")) {
    return "Users could not be synced. Please try again.";
  }
  if (path.includes("/chatgateway/flow-onboarding")) {
    return "Flow onboarding could not be completed. Please try again.";
  }
  return null;
}

function friendlyStatusMessage(status: number): string {
  if (status >= 500) {
    return "A server error occurred. Please try again later or contact support if the problem continues.";
  }
  if (status === 404) return "The requested service or resource was not found.";
  if (status === 409) return "This configuration conflicts with an existing record.";
  if (status === 400) return "The request was invalid. Please check your settings and try again.";
  if (status === 401 || status === 403) {
    return "You are not allowed to perform this action.";
  }
  if (status >= 400) return "The request could not be completed. Please try again.";
  return "Something went wrong. Please try again.";
}

function isGenericHttpErrorText(value: string): boolean {
  return GENERIC_HTTP_ERRORS.has(value.trim().toLowerCase());
}

/** Turn gateway / JSON error envelopes into operator-friendly copy. */
function humanizeBackendEnvelope(
  raw: string,
  context?: { status?: number; path?: string },
): string | null {
  const gatewayMatch = raw.match(GATEWAY_ERROR_PREFIX);
  const jsonBody = tryParseJsonObject(raw);
  if (!gatewayMatch && !jsonBody) return null;

  const status = readStatus(jsonBody ?? {}, context?.status ?? Number(gatewayMatch?.[1]));
  const path = readStringField(jsonBody ?? {}, "path") ?? context?.path ?? "";
  const bodyMessage = readStringField(jsonBody ?? {}, "message");
  const bodyError = readStringField(jsonBody ?? {}, "error");

  if (bodyMessage && !isGenericHttpErrorText(bodyMessage) && !isTechnicalErrorMessage(bodyMessage)) {
    return bodyMessage;
  }

  if (path) {
    const pathMessage = friendlyPathMessage(path);
    if (pathMessage) return pathMessage;
  }

  if (bodyError && !isGenericHttpErrorText(bodyError) && !isTechnicalErrorMessage(bodyError)) {
    return bodyError;
  }

  if (status) return friendlyStatusMessage(status);
  return null;
}

function looksLikeRawBackendError(raw: string): boolean {
  return (
    GATEWAY_ERROR_PREFIX.test(raw) ||
    (raw.startsWith("{") && raw.includes('"status"')) ||
    raw.includes("INTERNAL_SERVER_ERROR")
  );
}

/**
 * Detect raw HTML error pages (e.g. an upstream 503 "Service Unavailable" page
 * from a gateway / Cloud Run). These should never be shown verbatim.
 */
function looksLikeHtmlErrorPage(raw: string): boolean {
  const lower = raw.toLowerCase();
  return (
    lower.includes("<html") ||
    lower.includes("<!doctype html") ||
    lower.includes("<body") ||
    lower.includes("<title")
  );
}

/** Pull an HTTP status code out of an HTML error page (title/heading/body). */
function extractStatusFromHtml(raw: string): number | undefined {
  const match = raw.match(/\b([1-5]\d{2})\b/);
  if (!match) return undefined;
  const status = Number(match[1]);
  return status >= 100 && status <= 599 ? status : undefined;
}

export interface SanitizeErrorContext {
  status?: number;
  path?: string;
}

/** Map raw API / Keycloak errors to user-facing copy. */
export function sanitizeApiErrorMessage(
  message: string | undefined | null,
  context?: SanitizeErrorContext,
): string {
  const raw = message?.trim();
  if (!raw) return "Something went wrong. Please try again.";

  if (raw === "invalidPasswordMinSpecialCharsMessage") {
    return "Invalid password: must contain at least 1 special characters.";
  }

  if (isTechnicalErrorMessage(raw)) {
    return extractSpringValidationMessage(raw) ?? "Please check your details and try again.";
  }

  // Never surface raw HTML error pages — map them to friendly copy by status.
  if (looksLikeHtmlErrorPage(raw)) {
    const status = context?.status ?? extractStatusFromHtml(raw) ?? 503;
    if (status === 503) {
      return "The service is temporarily unavailable. Please try again shortly, or contact support if the problem continues.";
    }
    return friendlyStatusMessage(status);
  }

  if (looksLikeRawBackendError(raw)) {
    return humanizeBackendEnvelope(raw, context) ?? friendlyStatusMessage(context?.status ?? 500);
  }

  return raw;
}

export function getApiErrorMessage(err: unknown): string | null {
  if (!err || typeof err !== "object" || !("message" in err)) return null;
  const api = err as ApiError;
  const msg = api.message;
  if (typeof msg !== "string" || !msg.trim()) return null;
  return sanitizeApiErrorMessage(msg, { status: api.status, path: api.path });
}
